import axios from 'axios'
import { ElMessage } from 'element-plus'

let isRedirectingToLogin = false

const getStoredToken = () => {
  const token = localStorage.getItem('token')
  if (!token || token === 'undefined' || token === 'null') {
    return ''
  }
  return token
}

const clearPersistedAuth = () => {
  localStorage.removeItem('token')
  localStorage.removeItem('userInfo')
}

const redirectToLogin = () => {
  if (typeof window === 'undefined' || isRedirectingToLogin) {
    return
  }

  isRedirectingToLogin = true

  const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`
  const redirectQuery = currentPath && !currentPath.startsWith('/login')
    ? `?redirect=${encodeURIComponent(currentPath)}`
    : ''

  window.location.replace(`/login${redirectQuery}`)
}

const handleUnauthorized = (message = '登录状态已失效，请重新登录') => {
  clearPersistedAuth()

  ElMessage({
    message,
    type: 'warning',
    duration: 3 * 1000
  })

  redirectToLogin()
}

const service = axios.create({
  baseURL: process.env.VUE_APP_BASE_API || '/api',
  timeout: 10000,
  withCredentials: true,
  headers: {
    'Cache-Control': 'no-cache',
    Pragma: 'no-cache'
  }
})

service.interceptors.request.use(
  config => {
    const token = getStoredToken()
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  error => {
    console.error('Request error:', error)
    return Promise.reject(error)
  }
)

service.interceptors.response.use(
  response => {
    // 文件下载类请求（blob/arraybuffer）直接放行，不走业务 code 校验
    const responseType = response.config?.responseType
    if (responseType === 'blob' || responseType === 'arraybuffer') {
      return response.data
    }

    const res = response.data

    const successCodes = response.config.successCodes || [200]
    if (!successCodes.includes(res.code)) {
      if (res.code === 401) {
        const message = res.message || '登录状态已失效，请重新登录'
        handleUnauthorized(message)
        return Promise.reject(new Error(message))
      }

      ElMessage({
        message: res.message || '请求失败',
        type: 'error',
        duration: 5 * 1000
      })

      return Promise.reject(new Error(res.message || '请求失败'))
    }

    return res
  },
  async error => {
    // 不记录完整 Axios 错误对象，避免请求体中的密码进入控制台。
    console.error('Response error:', error.response?.status || error.code || 'unknown')

    // 下载类请求失败时，后端返回的错误 JSON 也是 Blob，需解析出错误信息
    if (error.response?.data instanceof Blob && error.config?.responseType === 'blob') {
      try {
        const text = await error.response.data.text()
        const data = JSON.parse(text)
        error.response.data = data
      } catch (_) {
        // 解析失败保持原样，走默认错误提示
      }
    }

    if (error.response?.status === 401) {
      const unauthorizedMessage = error.response?.data?.message || '登录状态已失效，请重新登录'
      error.message = unauthorizedMessage
      error.__messageDisplayed = true
      handleUnauthorized(unauthorizedMessage)
      return Promise.reject(error)
    }

    let message = error.response?.data?.message || '请求失败'

    if (error.response && !error.response?.data?.message) {
      switch (error.response.status) {
        case 403:
          message = '拒绝访问'
          break
        case 404:
          message = '请求资源不存在'
          break
        case 500:
          message = '服务器内部错误'
          break
        default:
          message = `连接错误 ${error.response.status}`
      }
    } else if (!error.response) {
      message = '网络连接异常，请检查网络后重试'
    }

    ElMessage({
      message,
      type: 'error',
      duration: 5 * 1000
    })

    error.message = message
    error.__messageDisplayed = true
    return Promise.reject(error)
  }
)

export default service
